import { FC, useState } from 'react';
import { Check, ChevronDown, GraduationCap } from 'lucide-react';

import type { RequirementGroup } from '@teachur/http-client';
import { cn } from '@new/lib';

import { GeneralEducationCard } from '../GeneralEducationCard/GeneralEducationCard';
import { RequirementSection } from '../RequirementSection/RequirementSection';
import { groupPlan } from '../../lib/planView';

interface CompletedPlanProps {
  name: string;
  credits: number;
  institution?: string | null;
  groups?: RequirementGroup[];
}

export const CompletedPlan: FC<CompletedPlanProps> = ({
  name,
  credits,
  institution,
  groups = [],
}) => {
  const grouped = groupPlan({ groups });
  const hasGeneralEducation = grouped.generalEducation.length > 0;
  const hasCourses = groups.length > 0;
  const [expanded, setExpanded] = useState(false);
  const coursesCompleted = groups.flatMap((group) => group.courses).length;

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <div className="relative overflow-hidden rounded-xl border border-(--neutral-black-NB200) border-l-4! border-l-(--success-S600)! bg-(--neutral-white-NW1000)">
      <span className="absolute top-0 right-[5%] flex items-center gap-1 rounded-b-[10px] bg-(--success-S600) px-2.5 py-1 text-[11px]! font-semibold uppercase tracking-wide text-(--neutral-white-NW1000)!">
        <Check className="size-3" />
        Completed
      </span>
      <div className="flex items-center gap-4 px-5 py-6 sm:px-6">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-(--success-S600)">
          <GraduationCap className="size-5 text-(--neutral-white-NW1000)" />
        </span>
        <span className="[font-size:var(--fs-p3)]! font-semibold text-(--neutral-black-NB900)!">
          {name}
        </span>
        <div className="ml-auto flex items-center gap-6">
          <p className="m-0! leading-none">
            <span className="[font-size:var(--fs-p2)]! font-bold text-(--neutral-black-NB900)!">
              {credits}
            </span>
            <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)!">
              {' '}
              credits earned
            </span>
          </p>
          {hasCourses ? (
            <p className="m-0! leading-none">
              <span className="[font-size:var(--fs-p2)]! font-bold text-(--neutral-black-NB900)!">
                {coursesCompleted}
              </span>
              <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)!">
                {' '}
                courses completed
              </span>
            </p>
          ) : null}
          {hasCourses ? (
            <button
              type="button"
              onClick={toggleExpanded}
              aria-expanded={expanded}
              aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
              className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full! border! border-(--success-S300)! bg-(--neutral-white-NW1000)"
            >
              <ChevronDown
                className={cn(
                  'size-4 text-(--success-S700) transition-transform',
                  expanded && 'rotate-180'
                )}
              />
            </button>
          ) : null}
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 bg-(--success-S200) px-5 py-2 sm:px-6">
        <span className="[font-size:var(--fs-l5)]! font-medium text-(--success-S700)!">
          Awarded by {institution || 'Newlane University'}
        </span>
        <span className="[font-size:var(--fs-l5)]! font-medium text-(--success-S700)!">
          All requirements fulfilled
        </span>
      </div>
      {expanded && hasCourses ? (
        <div className="flex flex-col gap-4 border-t border-(--neutral-black-NB200) p-4 sm:p-5">
          {hasGeneralEducation ? (
            <GeneralEducationCard
              groups={grouped.generalEducation}
              showBadges={false}
              showProgress={false}
            />
          ) : null}

          {grouped.required.map((group) => (
            <RequirementSection
              key={group.id}
              group={group}
              showBadges={false}
              showProgress={false}
            />
          ))}

          {grouped.electives.map((group) => (
            <RequirementSection
              key={group.id}
              group={group}
              title="General Electives"
              showBadges={false}
              showProgress={false}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};
